<template>
  <NotFound v-if="isNotFound" />
  <div v-else-if="page" class="px-4 pt-12 md:py-16 mx-auto max-w-6xl">
    <div
      id="residency-header"
      class="flex flex-col items-start justify-start gap-4 md:flex-row md:items-end md:justify-between"
    >
      <div id="residency-details">
        <p v-if="page.tagline" id="residency-tagline" class="text-fg-disclaimer">
          {{ page.tagline }}
        </p>
        <p v-else id="residency-tagline-fallback" class="text-fg-disclaimer">
          {{ taglineFallback }}
        </p>
        <h1 id="residency-name" class="text-4xl font-semibold font-heading">
          {{ page.name }}
        </h1>
        <p v-if="page.start_date" id="residency-dates" class="text-button-guest-primary text-lg">
          <span>{{ settingStore.fullDate(page.start_date) }}</span>
          <span v-if="showEndDate"> - {{ settingStore.fullDate(page.end_date) }}</span>
        </p>
      </div>

      <!--BUY TICKETS BUTTON-->
      <a
        class="performance-button py-3 px-6 rounded font-semibold text-base font-button bg-button-guest-primary text-button-label min-w-[150px] max-w-[200px] truncate uppercase text-center"
        id="buy-tickets-button"
        href="#buy-tickets-list"
      >
        See Dates & Times
      </a>
    </div>

    <!--SHOW IMAGE-->
    <div v-if="show" class="mt-6 w-full">
      <img
        id="residency-image"
        :src="show.srcset.rectXl.src"
        :srcset="buildShowSrcset(show.srcset)"
        sizes="(min-width: 1152px) 1120px, calc(100vw - 2rem)"
        :alt="show.name"
        class="w-full h-auto"
      />
    </div>

    <!--INTRO + AUDIO-->
    <div
      v-if="page.intro || audioReady"
      class="mt-6 flex flex-col gap-8 lg:flex-row lg:items-start"
    >
      <p v-if="page.intro" id="residency-intro" class="whitespace-pre-wrap lg:grow">
        {{ page.intro }}
      </p>
      <!--AUDIO-->
      <div v-if="audioReady" class="lg:w-2/5 2xl:w-1/3 lg:ml-auto shrink-0">
        <GuestAudioPlayer :audio="page.audio" />
      </div>
    </div>

    <!--PERFORMANCE LIST-->
    <div
      id="buy-tickets-list"
      ref="ticketListEl"
      tabindex="-1"
      class="p-6 sm:p-8 md:p-16 mt-6 border border-linear-g-primary"
    >
      <h2 class="text-2xl font-semibold font-heading">Buy Tickets</h2>
      <template v-if="displayShows.length > 0">
        <div
          v-for="show in displayShows"
          :key="show.id"
          :id="`show-${show.id}-date-${show.date}`"
          class="py-4 border-b border-linear-g-primary flex flex-col gap-1 items-start justify-start"
        >
          <div class="show-header">
            <p class="text-sm line-clamp-1">{{ show.name }}</p>
            <p>{{ settingStore.mediumDate(show.datetime) }}</p>
          </div>
          <div class="flex gap-2 justify-end items-start flex-wrap w-full">
            <PerformanceButton
              v-for="performance in show.performances"
              :key="performance.id"
              :performance="performance"
              :error="error"
              :id="`performance-${performance.id}`"
              :forcePurchase="
                Boolean(inviteCode) || checkoutStore.isPerformanceUnlockedByInviteCode(performance)
              "
            />
          </div>
        </div>
      </template>
      <p v-else>No upcoming shows</p>
    </div>

    <!--SOCIAL PROOF-->
    <blockquote
      v-if="page.social_proof"
      id="residency-social-proof"
      class="mt-8 border-l-4 border-button-guest-primary pl-4 italic text-lg whitespace-pre-wrap"
    >
      {{ page.social_proof }}
    </blockquote>

    <div class="mt-12 pt-10 flex flex-col-reverse gap-8 lg:flex-row">
      <div v-if="videoReady || show" class="flex flex-col gap-2 lg:grow">
        <!--HOSTED VIDEO-->
        <GuestVideoPlayer v-if="videoReady" :video="page.video" />
        <!--YOUTUBE VIDEO URL (fallback when there is no hosted video)-->
        <div v-else-if="youtubeEmbedSrc" id="residency-video" class="aspect-video w-full">
          <iframe
            :src="youtubeEmbedSrc"
            :title="`${page.name} video`"
            class="w-full h-full"
            loading="lazy"
            referrerpolicy="strict-origin-when-cross-origin"
            sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox"
            :allow="YOUTUBE_IFRAME_ALLOW"
            allowfullscreen
          ></iframe>
        </div>
      </div>
      <div class="lg:w-60 flex flex-col gap-2">
        <!--SHARE-->
        <ShareButton
          id="residency-share-button"
          :url="shareUrl"
          :title="page.name"
          :text="`${page.name} at ${venue_name}`"
        />

        <!--EXTERNAL LINKS-->
        <div v-if="sortedExternalLinks.length > 0" id="residency-external-links">
          <ul class="flex flex-col gap-2">
            <li
              v-for="link in sortedExternalLinks"
              :key="link.id ?? `${link.position}-${link.url}`"
            >
              <a
                :id="`residency-external-link-${link.id ?? link.position}`"
                :href="safeHref(link.url)"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-2 text-secondary hover:opacity-80"
              >
                <component :is="iconComponentByName[iconFor(link)]" class="w-5 h-5" />
                <span>{{ link.label }}</span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!--SHOW DESCRIPTION-->
    <div
      v-if="show"
      id="residency-description"
      class="mt-8 whitespace-pre-wrap"
      v-html="sanitizedDescription"
    />

    <!--ABOUT VENUE-->
    <section v-if="about_venue" id="residency-about-venue" class="mt-16">
      <h2 class="text-2xl font-semibold font-heading">About {{ venue_name }}</h2>
      <p class="mt-6 whitespace-pre-wrap">{{ about_venue }}</p>
    </section>

    <div class="hidden mt-16 md:flex items-start justify-start">
      <!--BUY TICKETS BUTTON-->
      <a
        class="performance-button py-3 px-6 rounded font-semibold text-base font-button bg-button-guest-primary text-button-label min-w-[150px] max-w-[200px] truncate uppercase text-center"
        id="buy-tickets-button-bottom"
        href="#buy-tickets-list"
      >
        See Dates & Times
      </a>
    </div>

    <!--MOBILE STICKY BUY TICKETS BUTTON-->
    <Transition
      enter-active-class="transition duration-200 ease-out motion-reduce:transition-none"
      enter-from-class="translate-y-full opacity-0"
      leave-active-class="transition duration-200 ease-in motion-reduce:transition-none"
      leave-to-class="translate-y-full opacity-0"
    >
      <div
        v-if="showStickyCta"
        id="residency-sticky-cta"
        class="md:hidden fixed inset-x-0 bottom-0 z-20 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-linear-g-primary bg-body-primary"
      >
        <a
          class="performance-button block w-full py-3 px-6 rounded font-semibold text-base font-button bg-button-guest-primary text-button-label truncate uppercase text-center"
          href="#buy-tickets-list"
          id="buy-tickets-button-sticky"
        >
          See Dates & Times
        </a>
      </div>
    </Transition>
  </div>
  <div v-else class="container px-4 py-12 md:py-16 mx-auto">
    <p>{{ loadingText }}</p>
  </div>
</template>

<script lang="ts" setup>
import { useIntersectionObserver } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import { buildShowSrcset } from '@/models'
import GuestAudioPlayer from '@/modules/guestUi/components/molecules/GuestAudioPlayer.vue'
import GuestVideoPlayer from '@/modules/guestUi/components/molecules/GuestVideoPlayer.vue'
import ShareButton from '@/modules/guestUi/components/molecules/ShareButton.vue'
import PerformanceButton from '@/modules/guestUi/components/organisms/PerformanceButton.vue'
import { LandingPageContent } from '@/modules/guestUi/interfaces/landingPage'
import { DisplayShow } from '@/modules/guestUi/interfaces/shows'
import NotFound from '@/modules/guestUi/pages/NotFound.vue'
import {
  DEFAULT_RESIDENCY_LINK_ICON,
  ResidencyLinkIconName,
  iconComponentByName,
  isResidencyLinkIconName,
} from '@/modules/residency/constants/ResidencyExternalLinkIcons'
import { isVideoAssetReady } from '@/modules/residency/utils/media'
import { upcomingPerformances } from '@/modules/residency/utils/performances'
import { getYouTubeEmbedUrl } from '@/modules/residency/utils/youtube'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { ErrorData, blankError } from '@/utils/errors'
import { sanitizeHtmlForClient } from '@/utils/sanitize'
import { sortBy } from '@/utils/utils'

import { ResidencyExternalLinkRead, ShowRead } from '@generated/types'

const props = defineProps<{
  page: LandingPageContent | null
  isNotFound: boolean
  shareUrl: string
  taglineFallback: string
  loadingText: string
}>()

const error = ref<ErrorData>(blankError())
const checkoutStore = useCheckoutStore()
const settingStore = useSettingStore()
const { venue_name, about_venue } = storeToRefs(settingStore)

const route = useRoute()
const inviteCode = computed<string | undefined>(() => route.query.invite as string | undefined)
onMounted(async () => {
  await checkoutStore.prepareCheckout(inviteCode.value)
})

const show = computed<ShowRead | undefined>(() => props.page?.show)
const showEndDate = computed<boolean>(
  () => !!props.page?.end_date && props.page.end_date !== props.page.start_date,
)
const audioReady = computed<boolean>(() => !!props.page?.audio?.mux_playback_id)
const videoReady = computed<boolean>(() => isVideoAssetReady(props.page?.video))
const sanitizedDescription = computed(() =>
  sanitizeHtmlForClient(props.page?.description || show.value?.description),
)

const youtubeEmbedSrc = computed<string | null>(() =>
  getYouTubeEmbedUrl(props.page?.video_url ?? ''),
)

const sortedExternalLinks = computed<ResidencyExternalLinkRead[]>(() =>
  [...(props.page?.external_links ?? [])].sort((a, b) => a.position - b.position),
)

function iconFor(link: ResidencyExternalLinkRead): ResidencyLinkIconName {
  return isResidencyLinkIconName(link.icon) ? link.icon : DEFAULT_RESIDENCY_LINK_ICON
}

function safeHref(url: string): string {
  return /^https?:\/\//i.test(url) ? url : '#'
}

const YOUTUBE_IFRAME_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share'

const displayShows = computed<DisplayShow[]>(function () {
  if (!props.page) return []

  const shows: DisplayShow[] = []
  for (const performance of upcomingPerformances(props.page.performances, settingStore.timezone)) {
    const performanceDate = settingStore.dateIsoStr(performance.datetime)
    const show = shows.find(show => show.date === performanceDate && show.id == performance.show_id)

    if (show) {
      show.performances.push(performance)
      sortBy(show.performances, 'datetime')
    } else {
      const desc = performance.show?.description || ''
      shows.push({
        ...performance.show,
        id: performance.show_id,
        date: performanceDate,
        datetime: performance.datetime,
        dateStr: settingStore.mediumDate(performance.datetime),
        description: desc,
        position: performance.show?.position || [],
        performances: [performance],
      })
    }
  }

  return sortBy(shows, 'datetime')
})

const ticketListEl = ref<HTMLElement | null>(null)
const showStickyCta = ref(false)
const ticketListObserver = useIntersectionObserver(
  ticketListEl,
  ([entry]) => {
    showStickyCta.value = !entry?.isIntersecting
  },
  { rootMargin: '-100px' },
)
onMounted(() => {
  if (!ticketListObserver.isSupported.value) showStickyCta.value = true
})
</script>

<style>
@media (prefers-reduced-motion: no-preference) {
  html {
    scroll-behavior: smooth;
  }
}
</style>
